// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Require environment variables - no hardcoded fallback to prevent using wrong database
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY;

// Detect if we're in production build
const isProduction = import.meta.env.MODE === 'production' || import.meta.env.PROD;

// Validate configuration - fail loudly if missing
if (!SUPABASE_URL) {
  const errorMsg = isProduction
    ? 'Database connection error: Missing VITE_SUPABASE_URL. This must be set during the build process. Please ensure environment variables are configured in your deployment platform (CI/CD, hosting service, etc.) before building.'
    : 'Missing VITE_SUPABASE_URL environment variable. Please set it in your .env file.';
  console.error('❌', errorMsg);
  console.error('💡 Tip: Vite embeds environment variables at build time. Make sure your .env file exists and contains VITE_SUPABASE_URL before running npm run build.');
  throw new Error(errorMsg);
}

if (!SUPABASE_PUBLISHABLE_KEY) {
  const errorMsg = isProduction
    ? 'Database connection error: Missing VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_ANON_KEY. This must be set during the build process. Please ensure environment variables are configured in your deployment platform before building.'
    : 'Missing VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_ANON_KEY environment variable. Please set it in your .env file.';
  console.error('❌', errorMsg);
  console.error('💡 Tip: Vite embeds environment variables at build time. Make sure your .env file exists and contains VITE_SUPABASE_PUBLISHABLE_KEY or VITE_SUPABASE_ANON_KEY before running npm run build.');
  throw new Error(errorMsg);
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// In-memory lock map to track active locks
// This prevents concurrent auth operations that could cause race conditions
const activeLocks = new Map<string, Promise<unknown>>();

// Custom browser lock implementation that provides serialization without using navigator.locks API
// This resolves issues where the browser lock API hangs indefinitely in some environments
// while still preventing race conditions from concurrent auth requests
const debugLock = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  acquireLock: async (name: string, timeout: number, fn: () => Promise<any>) => {
    // Check if there's already an active lock for this name
    const existingLock = activeLocks.get(name);
    
    if (existingLock) {
      console.log(`[Supabase] Waiting for existing lock: ${name}`);
      try {
        // Wait for the existing lock to complete before proceeding
        await existingLock;
      } catch (e) {
        // Ignore errors from previous lock - we'll try our own operation
        console.log(`[Supabase] Previous lock completed with error, proceeding: ${name}`);
      }
    }
    
    // Create a new lock promise for this operation
    const lockPromise = (async () => {
      try {
        console.log(`[Supabase] Acquiring lock: ${name}`);
        const result = await fn();
        return result;
      } catch (e) {
        console.error(`[Supabase] Lock execution error for ${name}:`, e);
        throw e;
      } finally {
        // Remove lock from map when done
        activeLocks.delete(name);
        console.log(`[Supabase] Released lock: ${name}`);
      }
    })();
    
    // Store the lock promise
    activeLocks.set(name, lockPromise);
    
    // Handle timeout: -1 means no timeout (wait indefinitely)
    // Only apply timeout if it's a positive number
    if (timeout > 0) {
      // Set a timeout to prevent locks from hanging indefinitely
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          activeLocks.delete(name);
          reject(new Error(`Lock timeout after ${timeout}ms for ${name}`));
        }, timeout);
      });
      
      // Race between the operation and timeout
      return Promise.race([lockPromise, timeoutPromise]);
    } else {
      // No timeout - just return the lock promise directly
      // This handles the case where Supabase passes -1 (infinite timeout)
      return lockPromise;
    }
  }
};

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY || '', {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    lock: typeof window !== 'undefined' ? debugLock.acquireLock : undefined,
  }
});